import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, Shield, Zap, Palette, Globe, Smartphone } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const HaiUnProgetto = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-purple-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            🥃 Hai un progetto?
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-purple-100">
            Trasforma la tua idea in un brand autentico
          </h2>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
            Hai in mente un progetto particolarmente importante?<br />
            Mettiamo a tua disposizione il nostro team di marketing, design e produzione per trasformare la tua idea in un prodotto reale, pronto per il mercato.
          </p>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mt-6">
            Che si tratti di un nuovo liquore personalizzato, di un marchio da lanciare nella ristorazione o nella GDO, oppure di una linea esclusiva per eventi e locali, ti seguiamo passo dopo passo in ogni fase.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        
        {/* Brand Identity Section */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Dal naming alla bottiglia: costruiamo la tua identità
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              Il nostro servizio non si ferma alla produzione del liquore.<br />
              Sviluppiamo l'intero progetto di comunicazione, dalla strategia alla realizzazione:
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Palette className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">💡 Naming e storytelling</h3>
              </div>
              <p className="text-gray-600">
                Ti aiutiamo a trovare il nome perfetto e la storia che lo rende memorabile.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Award className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">🎨 Brand identity e design visivo</h3>
              </div>
              <p className="text-gray-600">
                Creiamo loghi, etichette, packaging e materiali coordinati.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Globe className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">🌐 Siti web ed e-commerce</h3>
              </div>
              <p className="text-gray-600">
                Realizziamo la tua presenza digitale per vendere online in modo professionale.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Smartphone className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">📱 Contenuti per i social media</h3>
              </div>
              <p className="text-gray-600">
                Creiamo foto, video e testi che raccontano il tuo brand con stile e coerenza.
              </p>
            </div>
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* Complete Project Section */}
        <section className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Non solo un liquore. Un progetto completo.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
            Con IlTuoAmaro.it non realizzi solo una bottiglia: costruisci un'identità.<br />
            Un marchio che parla di te, del tuo territorio e della tua visione.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Grazie all'esperienza del Liquorificio Lacinio, specializzato nella produzione artigianale di amari, gin e liquori premium, uniamo creatività, competenza e tecnologia per offrirti una soluzione chiavi in mano — dalla prima idea fino al prodotto finito.
          </p>
        </section>

        <hr className="border-gray-200" />

        {/* Value Added Section */}
        <section>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            🎯 Il nostro valore aggiunto
          </h2>
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
              <p className="text-lg text-gray-700">Oltre 60 anni di esperienza nel mondo degli spirits.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
              <p className="text-lg text-gray-700">Produzione artigianale certificata e flessibile.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
              <p className="text-lg text-gray-700">Team grafico e marketing interno.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
              <p className="text-lg text-gray-700">Supporto strategico per il posizionamento del tuo brand.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
              <p className="text-lg text-gray-700">Possibilità di piccole tirature per testare il mercato.</p>
            </div>
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* Features Grid */}
        <section>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <Zap className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🚀 Alta capacità produttiva</h3>
              <p className="text-gray-600">
                Produciamo fino a 500 bottiglie all'ora, garantendo rapidità e continuità anche per grandi ordini.
              </p>
            </div>
            
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <Users className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎨 Team interno di marketing & grafica</h3>
              <p className="text-gray-600">
                Un team dedicato ti segue passo passo: dall'idea alla realizzazione dell'etichetta, fino al supporto per il lancio commerciale.
              </p>
            </div>
            
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <Award className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🏆 Progetti di successo</h3>
              <p className="text-gray-600">
                Abbiamo già realizzato brand e prodotti premiati a livello nazionale e internazionale, portando valore aggiunto a chi si affida a noi.
              </p>
            </div>
            
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <Shield className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🍃 Liquori artigianali</h3>
              <p className="text-gray-600">
                Ogni bottiglia nasce da ricette curate e infusi naturali, mantenendo il carattere autentico e distintivo dei nostri prodotti.
              </p>
            </div>
            
            <div className="text-center p-6 bg-purple-50 rounded-xl md:col-span-2 lg:col-span-1">
              <Palette className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔎 Piccole tirature per testare il mercato</h3>
              <p className="text-gray-600">
                Ti permettiamo di partire con lotti ridotti, ideali per validare il tuo business senza rischi eccessivi.
              </p>
            </div>
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-2xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            💬 Parliamone insieme
          </h2>
          <p className="text-xl mb-8">
            Ogni grande prodotto nasce da una buona conversazione.<br />
            Raccontaci la tua idea e scopri come possiamo renderla realtà.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/contatti"
              className="inline-flex items-center px-8 py-4 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              👉 Contattaci per una consulenza gratuita
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <p className="text-purple-100">
              Oppure scrivici a{' '}
              <a href="mailto:info@iltuoamaro.it" className="text-white underline hover:text-purple-100">
                info@iltuoamaro.it
              </a>
            </p>
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
};

export default HaiUnProgetto;